<template>
  <div style="max-width: 1200px">
    <ProcessBarModal
      :title="$t('Uploading file on the server')"
      :show-modal="showUploadModal"
      :step="uploadStep"
      :file-name="uploadingFileName"
      :progress="uploadProgress"
      :error-text="uploadError"
      :steps-text="{
        upload: $t('Uploading file on the server'),
        inject: $t('Injecting members'),
        success: $t('Injected members'),
        error: $t('Something went wrong'),
      }"
      @close="() => (showUploadModal = false)"
    />
    <CModal
      centered
      :show.sync="showModal"
      :draggable="false"
      :showFooter="false"
    >
      <CRow class="mt-2">
        <CCol col="12" class="ml-4">
          {{ $t("You are about to modify the list of campaign members.") }}
        </CCol>
      </CRow>
      <CRow class="mt-3 modal-footer">
        <CButton
          class="mz-button v2-mz-btn mr-2"
          color="primary"
          @click="updateCampaignMembers"
        >
          {{ $t("Confirm") }}
        </CButton>
      </CRow>
    </CModal>
    <CRow class="w-100">
      <ul class="pages-nav">
        <li
          v-for="page in pages"
          :key="page.page"
          @click="switchPage(page.page)"
          :class="{ 'is-current-page': isCurrentPage(page.page) }"
        >
          {{ $t(`${page.name}`) }}
        </li>
      </ul>
    </CRow>
    <CRow class="w-100">
      <CCard v-if="current_page === 'campaign_details'" class="w-75">
        <CCardBody>
          <div class="w-100 text-right justify-content-end mb-1">
            <CButton
              v-if="hasAccessComplete"
              class="mz-button v2-mz-btn mr-2"
              color="primary"
              @click="updateCampaign"
              :disabled="
                !hasAccessAdmin() && (updating_campaign || closed_campaign)
              "
            >
              <DotsLoader v-if="updating_campaign" />
              <span v-else>
                {{ $t("Save") }}
                <em class="ml-2 mz-icon icon-save color-inherit" />
              </span>
            </CButton>
          </div>
          <CForm class="d-flex flex-column gap-8">
            <div>
              <label>
                <div class="title">{{ $t("Campaign identification") }}</div>
              </label>
              <div>
                <div class="label">{{ $t("Campaign ID") }}*</div>
                <CInput
                  v-if="campaign_id"
                  v-model="campaign_id"
                  :disabled="true"
                />
              </div>
            </div>
            <hr />
            <div>
              <label>
                <div class="title">{{ $t("Dates") }}</div>
              </label>
              <CRow>
                <CCol col="6" class="p-1">
                  <div class="label">{{ $t("Start Date") }}*</div>
                  <MzDatePicker
                    v-if="campaign_data.campaign"
                    class="input-group"
                    v-model="campaign_data.campaign.date_start"
                    :config="{ format: 'YYYY-MM-DD' }"
                    placeholder="YYYY-MM-DD"
                    :disabled="!hasAccessAdmin() && closed_campaign"
                  />
                </CCol>
                <CCol col="6" class="p-1">
                  <div class="label">{{ $t("Close Date") }}*</div>
                  <MzDatePicker
                    v-if="campaign_data.campaign"
                    class="input-group"
                    v-model="campaign_data.campaign.date_end"
                    :config="{ format: 'YYYY-MM-DD' }"
                    placeholder="YYYY-MM-DD"
                    :disabled="!hasAccessAdmin() && closed_campaign"
                  />
                </CCol>
              </CRow>
            </div>
            <hr />
            <div>
              <label>
                <div class="title">{{ $t("CCR Group") }}</div>
              </label>
              <CRow>
                <span class="mb-3 col-2">
                  {{ $t("CCR Group n") }}
                </span>
                <MzSelect
                  class="mb-3 col-3"
                  :options="coveryear_ids"
                  :value.sync="selected_CCR_group"
                  @input="handleCCRGroupChange"
                />
              </CRow>
              <label>
                <div class="title">{{ $t("Gareat Rates") }}</div>
              </label>
              <CRow>
                <span class="mb-3 col-2">
                  {{ $t("Appendix I") }}
                </span>
                <MzSelect
                  class="mb-3 col-3"
                  :options="coveryear_ids"
                  :value.sync="selected_appendix_i"
                  @input="handleAppendixIChange"
                />
              </CRow>
              <CRow>
                <span class="mb-3 col-2">
                  {{ $t("Appendix II") }}
                </span>
                <MzSelect
                  class="mb-3 col-3"
                  :options="coveryear_ids"
                  :value.sync="selected_appendix_ii"
                  @input="handleAppendixIIChange"
                />
              </CRow>
              <CRow>
                <span class="mb-3 col-2">
                  {{ $t("Appendix III") }}
                </span>
                <MzSelect
                  class="mb-3 col-3"
                  :options="coveryear_ids"
                  :value.sync="selected_appendix_iii"
                  @input="handleAppendixIIIChange"
                      />
                </CRow>
              </div>
          </CForm>
          <CRow class="w-100 mt-2 p-3">
            {{ $t("The campaign configuration was") }}&nbsp;
            <span v-if="campaign_data.campaign.user_modification">
              {{ $t("updated by") }}
              {{ campaign_data.campaign.user_modification }}
              {{ $t("on") }}
              {{ formatDate(campaign_data.campaign.date_modification) }}
            </span>
            <span v-else>
              {{ $t("created by") }}
              {{ campaign_data.campaign.user_creation }}
              {{ $t("on") }}
              {{ formatDate(campaign_data.campaign.date_creation) }}
            </span>
          </CRow>
        </CCardBody>
      </CCard>
      <CCard v-if="current_page === 'declaring_members'" class="w-100">
        <CCardBody>
          <CRow class="w-100 text-right justify-content-end mb-1">
            <CCol col="5" class="h-100 mt-auto mb-auto">
              <CRow
                class="w-100 text-right justify-content-end"
                v-if="can_upload_campaign_members_file && !has_members"
              >
                {{ $t("Template to download") }}:
                <a
                  class="template-file ml-2"
                  @click="
                    $downloadFile({
                      downloadFileName: 'Campagne_Adherents.xlsx',
                      url: MEMBERS_ENDPOINTS.download_campaign_members_template,
                    })
                  "
                >
                  campagne_adherents.xlsx
                </a>
              </CRow>
            </CCol>
            <CCol col="7">
              <CRow>
                <CCol col="6" class="pr-0">
                  <file-dragdrop
                    v-if="can_upload_campaign_members_file && !has_members"
                    :disabled="!can_upload_campaign_members_file"
                    :title="$t('Upload campaign members file')"
                    @upload="handleUpload"
                  >
                    <template #body-content>
                      {{ $t("Only xlsx files allowed") }}
                    </template>
                    <template #upload-button>
                      <CButton
                      v-if="!has_members"
                        :disabled="
                          !can_upload_campaign_members_file ||
                          updating_campaign ||
                          closed_campaign ||
                          has_members
                        "
                        color="primary"
                        class="mz-button v2-mz-btn mz-button-custom-radius"
                      >
                        <DotsLoader v-if="uploadingFiles" />
                        <span v-else>
                          {{ $t("Upload") }}
                        </span>
                      </CButton>
                    </template>
                  </file-dragdrop>
                </CCol>
                <CCol col="3" class="pl-0">
                  <CButton
                    v-if="hasAccessComplete"
                    class="mz-button v2-mz-btn mz-button-custom-radius"
                    color="primary"
                    :disabled="!has_members"
                    @click="
                      $downloadFile({
                        url: MEMBERS_ENDPOINTS.download_campaign_members,
                        query: { id_campaign: campaign_id },
                      })
                    "
                  >
                    <span>
                      {{ $t("Download") }}
                    </span>
                  </CButton>
                </CCol>
                <CCol col="3" class="pl-0">
                  <CButton
                    v-if="hasAccessComplete"
                    class="mz-button v2-mz-btn mr-2"
                    color="primary"
                    @click="() => (showModal = true)"
                    :disabled="updating_campaign || closed_campaign"
                  >
                    <DotsLoader v-if="updating_campaign" />
                    <span v-else>
                      {{ $t("Save") }}
                    </span>
                  </CButton>
                </CCol>
              </CRow>
            </CCol>
          </CRow>
          <DotsLoader v-if="!campaign_member_data" />
          <table-component
            v-else
            :key="`declaring_members-${current_page}`"
            :columns="Object.keys(declaringMembersHeaders)"
            :headings="declaringMembersHeaders"
            ref="table_campaign_member"
            :filter-exclude-columns="[
              'code_gta',
              'member',
              'contact',
              'accounting_code',
              'declarer',
            ]"
            :data="campaign_member_data"
            :showFilterHelp="false"
            :settings="settings"
          >
            <template slot="code_gta" slot-scope="{ row }">
              {{ row.code_gda || row.id_member }}
            </template>
            <template slot="member" slot-scope="{ row }">
              {{ row.name }}
            </template>
            <template slot="contact" slot-scope="{ row }">
              {{ row.email || "-" }}
            </template>
            <template slot="accounting_code" slot-scope="{ row }">
              {{ row.code_cpt || "-" }}
            </template>

            <template slot="prev_ccr_group" slot-scope="{ row }">
              {{ row.prev_group_ccr || "-" }}
            </template>

            <template slot="ccr_group" slot-scope="{ row }">
              {{ row.group_ccr || "-" }}
            </template>
            <template slot="declarer" slot-scope="{ row }">
              <input
                type="checkbox"
                :value.sync="row.declarer"
                :checked="row.declarer"
                @change="handleRowUpdate(row, 'declarer', !row.declarer)"
                :disabled="closed_campaign"
              />
            </template>
            <template slot="filter__declarer">
              <input
                type="checkbox"
                :value.sync="selectAll"
                :checked="selectAll"
                @change="selectAllMembers()"
                :disabled="closed_campaign"
              />
            </template>
            <template
              v-for="filter in Object.keys(filter_table_data)"
              :slot="`filter__${filter}`"
            >
              <input
                class="form-control mz-input"
                :placeholder="`Filter By ${filter}`"
                v-model="filter_table_data[filter].value"
                @change="filterData()"
              />
            </template>
          </table-component>
        </CCardBody>
        <CRow class="w-100 mt-4 p-3 text-center d-flex justify-content-center">
            {{ $t("The campaign configuration was") }}&nbsp;
            <span v-if="campaign_data.campaign.user_modification">
              {{ $t("updated by") }}
              {{ campaign_data.campaign.user_modification }}
              {{ $t("on") }}
              {{ formatDate(campaign_data.campaign.date_modification) }}
            </span>
            <span v-else>
              {{ $t("created by") }}
              {{ campaign_data.campaign.user_creation }}
              {{ $t("on") }}
              {{ formatDate(campaign_data.campaign.date_creation) }}
            </span>
          </CRow>
      </CCard>
      <CCard v-if="current_page === 'monitoring'" class="w-100">
        <CCardHeader>
          <div class="card-header-actions w-100">
            <CButton
              class="mz-button v2-mz-btn mr-2"
              color="primary"
              style="float: right"
              @click="
                $downloadFile({
                  url: export_url,
                })
              "
            >
              {{ $t("Export") }}
            </CButton>
          </div>
        </CCardHeader>
        <CCardBody>
          <table-component
            :key="`monitoring-${current_page}`"
            :columns="Object.keys(monitoringHeaders)"
            :headings="monitoringHeaders"
            :sortExcludeColumns="Object.keys(monitoringHeaders)"
            :url="
              CAMPAIGNS_ENDPOINTS.campaign_members_files(
                $route.params.campaignId
              )
            "
            :show-top-number-of-records="false"
            :display-columns-selector="false"
            ref="table_files"
          >
            <template
              v-for="slot in Object.keys(file_types)"
              :slot="slot"
              slot-scope="{ row }"
            >
              <div v-for="item in getFilteredTypeItems(slot, row)">
                <p>{{ $t("Injected on") }}: {{ item.date_creation }},</p>
                <p>
                  {{ $t("by") }}
                  <CLink :to="{}"> {{ item.user_creation }}</CLink>
                  ,
                </p>
                <p>
                  {{ $t("Status") }}:
                  {{
                    $store.state.language === "en"
                      ? item.status
                      : item.status_fr
                  }}
                </p>
                <p>{{ $t("Modified on") }}: {{ item.date_modification }}</p>
                <p>
                  {{ $t("by") }}
                  <CLink :to="{}"> {{ item.user_modification }}</CLink>
                  ,
                </p>
              </div>
            </template>
          </table-component>
        </CCardBody>
      </CCard>
    </CRow>
  </div>
</template>

<script>
import { CAMPAIGNS_ENDPOINTS, CampaignServices } from "@/services/campaigns";
import { COVERYEARS_ENDPOINTS } from "@/services/coveryears";
import { MEMBERS_ENDPOINTS } from "@/services/members";
import { BranchesServices } from "@/services/branches";
import moment from "moment/moment";
import { ActiveUser } from "@/services/user";
import { userRoles } from "../../constants/userRoles";
import { getNumericalValue } from "@/utils/helpers";
import Task from "@/services/task";
import ProcessBarModal from "@/components/ProcessBarModal.vue";

export default {
  name: "CampaignTaux",
  components: { ProcessBarModal },
  data() {
    return {
      CAMPAIGNS_ENDPOINTS,
      COVERYEARS_ENDPOINTS,
      getNumericalValue,
      showModal: false,
      updating_campaign: false,
      file_types: {
        risk_at_address: "Risk at the address",
        annexei: "Appendix I",
        annexeii: "Appendix II",
        annexeiii: "Appendix III",
        etat: "Other file",
      },
      current_page: "campaign_details",
      campaign_id: this.$route.params.campaignId,
      campaign_data: {},
      currentTime: new Date(),
      campaign_member_data: [],
      has_members: false,
      useData: ActiveUser,
      selectAll: false,
      coveryear_ids: [],
      selected_CCR_group: null,
      selected_appendix_i: null,
      selected_appendix_ii: null,
      selected_appendix_iii: null,
      coveryear_data: {},
      pages: [
        {
          page: "campaign_details",
          name: "Campaign",
          only_admin: false,
        },
        {
          page: "declaring_members",
          name: "Declaring Members",
          only_admin: true,
        },
        {
          page: "monitoring",
          name: "Monitoring",
          only_admin: true,
        },
      ],
      row_size: 2,
      filter_table_data: {
        code_gta: {
          value: "",
          field: "code_gda",
        },
        member: {
          value: "",
          field: "name",
        },
        contact: {
          value: "",
          field: "email",
        },
        accounting_code: {
          value: "",
          field: "code_cpt",
        },
        ccr_group: {
          value: "",
          field: "group_ccr",
        },
      },
      selected_members: {},
      MEMBERS_ENDPOINTS,
      uploadingFiles: false,
      uploadProgress: 0,
      uploadStep: "upload",
      uploadingFileName: "",
      showUploadModal: false,
      uploadError: "",
      declaringMembersHeaders: {
        code_gta: this.$t("GTA Code"),
        member: this.$t("Member"),
        contact: "Contact",
        accounting_code: this.$t("Accounting Code"),
        prev_ccr_group: `${this.$t("CCR Group")} n-1`,
        ccr_group: this.$t("CCR Group"),
        declarer: this.$t("Declarer"),
      },
      monitoringHeaders: {
        code_gda: this.$t("members/GTA Code"),
        code_cpt: this.$t("Accounting Code"),
        name: this.$t("Member"),
        risk_at_address: this.$t("Risk at the address"),
        annexei: this.$t("Appendix I"),
        annexeii: this.$t("Appendix II"),
        annexeiii: this.$t("Appendix III"),
        etat: `${this.$t("State")} FR13-03`,
      },
    };
  },
  computed: {
    export_url() {
      return CAMPAIGNS_ENDPOINTS.campaign_members_export(
        this.campaign_data?.campaign?.id_campaign,
        this.$store.state.language
      );
    },
    settings() {
      const sortFunction = this.sortFunction;
      const customSorting = {};
      Object.entries(this.filter_table_data).forEach(([key, field_data]) => {
        customSorting[key] = function (ascending) {
          return function (a, b) {
            return sortFunction(a, b, field_data.field, ascending);
          };
        };
      });
      return { customSorting };
    },
    closed_campaign() {
      if (this.campaign_data?.campaign?.date_end) {
        return (
          new Date(this.campaign_data.campaign.date_end) < this.currentTime
        );
      }
      return false;
    },
    can_upload_campaign_members_file() {
      return !this.campaign_data?.campaign?.date_upload;
    },
        hasAccessComplete() {
      return !ActiveUser.isSuperUser();
    },
  },
  async mounted() {
    this.campaign_id = this.$route.params.campaignId || undefined;
    await this.filterPages();
    if (this.campaign_id) {
      await this.fetchCampaignMember(true);
      await this.fetchData();
    }
    this.coveryear_ids = this.coveryear_data.coveryear_ids.map(
      (item) => item.id_coveryear
    );
  },
  methods: {
    formatDate(date) {
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    },
    async handleUpload(files) {
      await Promise.all(Array.from(files).map((file) => this.uploadFile(file)));
      
    },
    handleCCRGroupChange(coveryear_id) {
      this.selected_CCR_group = coveryear_id;
    },
    handleAppendixIChange(coveryear_id) {
      this.selected_appendix_i = coveryear_id;
    },
    handleAppendixIIChange(coveryear_id) {
      this.selected_appendix_ii = coveryear_id;
    },
    handleAppendixIIIChange(coveryear_id) {
      this.selected_appendix_iii = coveryear_id;
    },
    async handleTaskResult(step) {
      this.uploadingFiles = false;
      this.uploadStep = step;
      await this.fetchCampaignMember(true);
      await this.fetchData();
    },
    handleOnProgress(value) {
      this.uploadProgress = value;
    },
    async uploadFile(file) {
      this.uploadingFiles = true;
      this.uploadError = "";
      this.uploadProgress = 0;
      this.uploadStep = "upload";
      this.uploadingFileName = file.name;
      this.showUploadModal = true;

      const query = {
        id_campaign: this.campaign_id,
      };

      const response = await this.$uploadFile({
        fileId: 120,
        file: file,
        url: this.MEMBERS_ENDPOINTS.inject_campaign_members,
        query: query,
        onProgress: this.handleOnProgress,
        showAlerts: false,
      });

      if (response && !response.error) {
        this.uploadStep = "inject";
        const task = new Task(response);
        task.on("result", (result) => {
          if (result?.error) {
            this.handleTaskResult("error");
            this.uploadError = result.error;
          } else this.handleTaskResult("success");
        });

        task.on("error", (error) => {
          this.handleTaskResult("error");
          this.uploadError = error.toString();
        });
      } else {
        this.uploadingFiles = false;
        this.uploadStep = "error";
        this.uploadError = response.error;
      }
    },
    sortFunction(a, b, field, ascending) {
      const lastA = a[field]?.toLowerCase();
      const lastB = b[field]?.toLowerCase();

      if (ascending) {
        return lastA >= lastB ? 1 : -1;
      }

      return lastA <= lastB ? 1 : -1;
    },
    async filterData() {
      await this.fetchCampaignMember();
    },
    getFilteredTypeItems(slot_name, row) {
      return row.files.filter(
        (item) => item.type === this.file_types[slot_name]
      );
    },
    async filterPages() {
      if (!this.hasAccessComplete) {
        this.pages = this.pages.filter((page) => !page.only_admin);
      }
    },
    hasAccessAdmin() {
      return ActiveUser.hasRole(userRoles.Admin);
    },
    isSuperUser() {
      return ActiveUser.user.role === userRoles.SuperUser;
    },
    switchPage(page) {
      this.current_page = page;
    },
    isCurrentPage(page) {
      return this.current_page === page;
    },
    handleRowUpdate(row, k, v) {
      const index = this.campaign_member_data.findIndex(
        (element) => element.id_member === row.id_member
      );
      this.campaign_member_data[index][k] = v;
      if (k === "declarer") {
        this.selected_members[this.campaign_member_data[index].id_member] = {
          ...this.campaign_member_data[index],
        };
      }
    },
    selectAllMembers() {
      this.selectAll = !this.selectAll;
      this.campaign_member_data.forEach((el) => {
        el["declarer"] = this.selectAll;
      });
      this.selected_members = this.campaign_member_data.reduce(
        (accumulator, campaign_member) => {
          accumulator[campaign_member.id_member] = { ...campaign_member };
          return accumulator;
        },
        {}
      );
    },
    isStartDateGreaterThanEndDate() {
      return (
        this.campaign_data &&
        this.campaign_data.campaign.date_start >=
          this.campaign_data.campaign.date_end
      );
    },
    async fetchCampaignMember(fetch_init_data = false) {
      const query = {};
      Object.values(this.filter_table_data).forEach((column_data) => {
        if (column_data.value && column_data.value !== "") {
          query[`search_${column_data.field}`] = column_data.value;
        }
      });
      const { data } = await this.$request({
        url: CAMPAIGNS_ENDPOINTS.all_campaign_member(this.campaign_id),
        query: {
          per_page: 10000,
          ...query,
        },
      });
      this.campaign_member_data = data.members;
      this.has_members = data.has_members;
      if (fetch_init_data) {
        this.campaign_member_data
          .filter((campaign_member) => campaign_member.declarer)
          .forEach((campaign_member) => {
            this.selected_members[campaign_member.id_member] = {
              ...campaign_member,
            };
          });
      }
    },
    async fetchData() {
      await this.fetchDataCampaign();
      await this.fetchDataCoveryear();
    },

    async fetchDataCampaign() {
      const { data } = await this.$request({
        url: CAMPAIGNS_ENDPOINTS.campaign_taux(this.campaign_id),
      });
      this.campaign_data = data;
      this.campaign_data.campaign.id_campaign =
        this.campaign_data.campaign.id_campaign.toString();
      console.log("campaign_data : ", this.campaign_data);
      this.selected_CCR_group =
        this.campaign_data?.campaign_coveryear[0]?.id_coveryear ?? null;
      this.selected_appendix_i =
        this.campaign_data?.campaign_coveryear[0]?.id_cover_year_appendix_i ??
        null;
      this.selected_appendix_ii =
        this.campaign_data?.campaign_coveryear[0]?.id_cover_year_appendix_ii ??
        null;
      this.selected_appendix_iii =
        this.campaign_data?.campaign_coveryear[0]?.id_cover_year_appendix_iii ??
        null;
    },

    async fetchDataCoveryear() {
      const { data } = await this.$request({
        url: COVERYEARS_ENDPOINTS.coveryear_CCR,
      });
      this.coveryear_data = data;
    },
    async updateCampaign() {
      this.updating_campaign = true;
      if (this.isStartDateGreaterThanEndDate()) {
        this.updating_campaign = false;
        alert(
          this.$t("The closure date should be greater than the start date"), {
              action: {
                  text: this.$t('Close'),
                  icon: 'close',
                  onClick: (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              }
          })

        return;
      }

      if (
        this.selected_CCR_group == null ||
        this.selected_appendix_i == null ||
        this.selected_appendix_ii == null ||
        this.selected_appendix_iii == null
      ) {
        this.updating_campaign = false;
        alert(this.$t("Please fill in all fields before saving"));
        return;
      }
      const today = new Date();

      const { res } = await CampaignServices.updateCampaign(this.campaign_id, {
        id_campaign: parseInt(this.campaign_id),
        date_start: this.campaign_data.campaign.date_start,
        date_end: this.campaign_data.campaign.date_end,
        date_modification: moment(today).format("YYYY-MM-DD HH:mm:ss"),
        user_modification: ActiveUser?.user?.email,
        id_coveryear: this.selected_CCR_group,
        id_cover_year_appendix_i: this.selected_appendix_i,
        id_cover_year_appendix_ii: this.selected_appendix_ii,
        id_cover_year_appendix_iii: this.selected_appendix_iii,
      });

      if (res.status === 200) {
        alert(this.$t("Saved Campaign Details"), {
              action: {
                  text: this.$t('Close'),
                  icon: 'close',
                  onClick: (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              }
          })
        await this.fetchData();
      }
      this.updating_campaign = false;
      await this.fetchCampaignMember();
    },
    async updateCampaignMembers() {
      this.showModal = false;
      this.updating_campaign = true;
      const campaignMembers = Object.values(this.selected_members)
        .filter((member) => member.declarer)
        .map((member) => ({
          ...member,
          group_ccr: this.campaign_member_data.find(
            (campaign_member) => campaign_member.id_member === member.id_member
          )?.group_ccr,
        }));
      const payload = {
        campaign_members: campaignMembers,
        user_creation: ActiveUser?.user?.email,
      };

      const { res } = await this.$request({
        url: CAMPAIGNS_ENDPOINTS.update_campaign_member(this.campaign_id),
        method: "POST",
        body: payload,
      });

      if (res.status === 200) {
        alert(this.$t("Saved Campaign Members"), {
              action: {
                  text: this.$t('Close'),
                  icon: 'close',
                  onClick: (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              }
          })

        await this.fetchCampaignMember();
      } else {
        alert(this.$t("Something went wrong"), {
              action: {
                  text: this.$t('Close'),
                  icon: 'close',
                  onClick: (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              }
          })

      }
      this.updating_campaign = false;
    },
  },
};
</script>

<style lang="scss">
.mz-button-custom-radius {
  border-radius: 10px !important;
}

.is-current-page {
  border-bottom: 2px solid #23145f;
}

.pages-nav {
  list-style: none;
  margin: 1rem 0;
  color: #23145f;

  li {
    font-size: 20px;
    font-weight: bold;
    display: inline;
    padding: 1rem;
    margin: 0 0.5rem;

    &:hover {
      cursor: pointer;
    }
  }
}

.arrow-buttons {
  display: none;
}

input[name="filter__ccr_group"] {
  display: none;
}

.modal-header {
  color: #fff;
  background-color: #23145f;
  border-color: #23145f;
}

.template-file {
  color: blue !important;
  text-decoration: underline !important;
  cursor: pointer;
}
</style>
